/**
 * Input — unified action map across keyboard, touch, gamepad, and any
 * future device.
 *
 * Game code should never read raw keycodes or gamepad indices. Instead it
 * asks for named actions:
 *
 *   this.sdk.input.action('fire')   // boolean
 *   this.sdk.input.axis('moveX')    // -1..1
 *
 * Input devices are modeled as **sources** that push state into a shared
 * registry. The default sources are:
 *
 *   - 'keyboard'  — installed automatically on `window` keydown/keyup
 *   - 'touch'     — VirtualJoystick + the original TouchButton
 *   - 'dpad'      — TouchDPad (8-way arcade pad)
 *   - 'gamepad'   — GamepadSource (W3C Gamepad API)
 *
 * Buttons across sources are OR'd together (any source pressing `fire` =
 * fire is on). Axes pick the source with the **largest magnitude**, so a
 * fully-deflected gamepad stick beats a half-pressed dpad slide, and a
 * fully-pressed keyboard arrow beats either of those when nothing else is
 * being touched.
 *
 * Keyboard listeners are attached to `window` so they survive Phaser scene
 * transitions — Phaser's per-scene keyboard plugin would silently go quiet
 * the moment the active scene changes.
 */
export const DEFAULT_BINDINGS = Object.freeze({
  keyboard: {
    up:    ['ArrowUp', 'KeyW'],
    down:  ['ArrowDown', 'KeyS'],
    left:  ['ArrowLeft', 'KeyA'],
    right: ['ArrowRight', 'KeyD'],
    fire:  ['Space', 'KeyJ'],
    alt:   ['KeyK'],
    pause: ['Escape', 'KeyP']
  }
});

export class Input {
  /**
   * @param {object} [opts]
   * @param {object} [opts.bindings=DEFAULT_BINDINGS]
   */
  constructor(opts = {}) {
    this.bindings = opts.bindings || DEFAULT_BINDINGS;

    /** @type {Set<string>} */
    this._heldKeys = new Set();
    /** @type {Map<string, { buttons: Map<string, boolean>, axes: Map<string, number> }>} */
    this._sources = new Map();

    this._installKeyboard();
  }

  // ─────────────────────────────────────────────────────── source registry

  _ensureSource(name) {
    let s = this._sources.get(name);
    if (!s) {
      s = { buttons: new Map(), axes: new Map() };
      this._sources.set(name, s);
    }
    return s;
  }

  /** External device pushes a named button state. */
  setSourceButton(source, action, down) {
    this._ensureSource(source).buttons.set(action, !!down);
  }

  /** External device pushes a named axis value, or `null` to clear. */
  setSourceAxis(source, axisName, value) {
    const s = this._ensureSource(source);
    if (value == null) {
      s.axes.delete(axisName);
    } else {
      s.axes.set(axisName, clamp(value, -1, 1));
    }
  }

  /** Wipe everything from one source (used by GamepadSource on disconnect). */
  clearSource(source) {
    const s = this._sources.get(source);
    if (!s) return;
    s.buttons.clear();
    s.axes.clear();
  }

  // ─────────────────────────────────────────────── backwards-compat aliases

  /** Used by VirtualJoystick + the existing TouchButton. */
  setVirtualButton(action, down) {
    this.setSourceButton('touch', action, down);
  }

  setVirtualAxis(axisName, value) {
    this.setSourceAxis('touch', axisName, value);
  }

  // ─────────────────────────────────────────────────────────── public reads

  /** True if any source (or the keyboard) currently presses this action. */
  action(name) {
    for (const [, s] of this._sources) {
      if (s.buttons.get(name)) return true;
    }
    const codes = this.bindings.keyboard[name];
    if (codes) {
      for (const c of codes) if (this._heldKeys.has(c)) return true;
    }
    return false;
  }

  /**
   * Returns the strongest axis value across all sources, with a keyboard
   * fallback that synthesizes -1/0/+1 from raw arrow-key state.
   *
   * Important: the keyboard fallback reads `_heldKeys` directly, not
   * `action(...)`. If we used `action()` here we'd OR in any synthetic
   * button state from gamepad sticks etc. and clobber the analog axis
   * value with a binary +/-1.
   */
  axis(name) {
    let best = 0;

    for (const [, s] of this._sources) {
      const v = s.axes.get(name);
      if (v != null && Math.abs(v) > Math.abs(best)) best = v;
    }

    if (name === 'moveX') {
      const kb = (this._kbHeld('right') ? 1 : 0) - (this._kbHeld('left') ? 1 : 0);
      if (Math.abs(kb) > Math.abs(best)) best = kb;
    }
    if (name === 'moveY') {
      const kb = (this._kbHeld('down') ? 1 : 0) - (this._kbHeld('up') ? 1 : 0);
      if (Math.abs(kb) > Math.abs(best)) best = kb;
    }

    return best;
  }

  _kbHeld(action) {
    const codes = this.bindings.keyboard[action];
    if (!codes) return false;
    for (const c of codes) if (this._heldKeys.has(c)) return true;
    return false;
  }

  /**
   * Snapshot used by the network layer when sending inputs over the wire.
   * Includes the standard movement actions plus AR-related fields (lookX/Y
   * from device orientation, shake/accel from device motion). Tracking
   * landmarks are NOT serialized — they're large and live on each peer's
   * own camera feed.
   */
  serialize() {
    return {
      // Movement
      up: this.action('up'),
      down: this.action('down'),
      left: this.action('left'),
      right: this.action('right'),
      moveX: this.axis('moveX'),
      moveY: this.axis('moveY'),
      // Standard actions
      fire: this.action('fire'),
      alt: this.action('alt'),
      // AR / device sensors
      lookX: this.axis('lookX'),
      lookY: this.axis('lookY'),
      shake: this.action('shake'),
      accelX: this.axis('accelX'),
      accelY: this.axis('accelY'),
      accelZ: this.axis('accelZ')
    };
  }

  /**
   * Heuristic: 'touch' on devices with a touchscreen, 'desktop' otherwise.
   * Used by ArcadeController to decide whether to render on-screen controls
   * by default.
   */
  get deviceHint() {
    if (typeof window === 'undefined') return 'unknown';
    if ('ontouchstart' in window) return 'touch';
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return 'touch';
    return 'desktop';
  }

  // ─────────────────────────────────────────────────────────────── internals

  _installKeyboard() {
    if (typeof window === 'undefined') return;
    window.addEventListener('keydown', (ev) => {
      this._heldKeys.add(ev.code);
      if (this._isGameKey(ev.code)) ev.preventDefault();
    }, { passive: false });
    window.addEventListener('keyup', (ev) => {
      this._heldKeys.delete(ev.code);
    });
    window.addEventListener('blur', () => this._heldKeys.clear());
  }

  _isGameKey(code) {
    for (const action of Object.keys(this.bindings.keyboard)) {
      if (this.bindings.keyboard[action].indexOf(code) !== -1) return true;
    }
    return false;
  }
}

function clamp(v, lo, hi) {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
